import { useDSDFiles } from "@/hooks/use-dsd-files";
import { useUserNames } from "@/hooks/queries/use-user-names";
import { DsdFile, DsdFileStatus } from "@/types/dsd";
import { getRelativeTime } from "@/utils/time";
import { getAuthorDisplayName } from "@/utils/user-display-name";
import { useMemo } from "react";
import {
  asReportPeriod,
  asReportType,
} from "@/components/team-workspace/report-list/group-reports";
import type { RecentReport } from "./recent-report-card";

/**
 * 최근 수정된 보고서 상위 `limit`개. 워크북 관리 화면과 홈 STEP4 패널이 같은 선별 규칙을 쓴다.
 * `isLoading` 동안은 목록이 비어 있어도 "없음"이 아니고, `isError` 면 조회 실패지 "없음"이 아니다.
 */
export const useRecentReports = (limit: number) => {
  const {
    data: dsdFiles,
    isLoading,
    isError,
    refetch,
  } = useDSDFiles({ status: DsdFileStatus.DSD_ANALYZED });

  const files = useMemo(() => dsdFiles ?? [], [dsdFiles]);
  // isLoading은 enabled로 막 전환된 렌더 틱에 아직 false일 수 있어(effect가 아직 안
  // 돌아서) 그 순간 이메일이 스켈레톤 없이 그려진다. 맵 데이터 유무로 직접 판단한다.
  const { data: userNamesMap } = useUserNames(
    useMemo(() => files.map((f) => f.creator), [files]),
  );
  const isAuthorLoading = files.length > 0 && !userNamesMap;

  const reports = useMemo<RecentReport[]>(() => {
    // 목록과 같은 표시용 이름(동명 보고서는 `(revision)` 부착). group-reports 참조.
    const toRecentReport = (file: DsdFile): RecentReport => ({
      id: file.id,
      name: file.label,
      financialStatementType: asReportType(file.financialStatementType),
      reportPeriod: asReportPeriod(file.reportQuarter),
      version: `V${file.versionNo}`,
      author: {
        name: getAuthorDisplayName(userNamesMap, file.creator),
        email: file.creator,
      },
      lastModified: getRelativeTime(file.updatedAt),
    });

    // 버전 스택은 최신본 한 건만 후보로 쓴다 — 누군가의 직전본으로 지목된 워크북은
    // 제외. 목록(group-reports.ts)의 leaf 판정과 같은 규칙이며, 없으면 같은 보고서의
    // V5·V4·V3 가 카드 3장을 모두 차지한다.
    const referencedAsPrev = new Set<string>();
    files.forEach((file) => {
      if (file.prevWorkbookId) referencedAsPrev.add(file.prevWorkbookId);
    });

    // 수정 시각은 DB updated_at 을 그대로 쓴다. 예전엔 updatedAt-createdAt 차이로
    // "사람이 손댄 것"을 가려냈지만, 그 차이가 재는 건 사람의 편집이 아니라 오토태깅이
    // 얼마나 돌았는지였다 — LLM 을 건너뛰는 버전 업데이트가 통째로 걸러져 최신 버전이
    // 카드에 뜨지 않았다(WXBRL-2168).
    return files
      .filter((file) => !referencedAsPrev.has(file.id))
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
      .slice(0, limit)
      .map(toRecentReport);
  }, [files, userNamesMap, limit]);

  return { reports, isAuthorLoading, isLoading, isError, refetch };
};
